import React, { useEffect, useMemo, useState } from "react";
import reportService from "../../services/reportService";

/* ===================================================== */
/* OVERVIEW TAB                                          */
/* ===================================================== */

export default function OverviewTab({ project, tasks = [] }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  /* ===================================================== */
  /* FETCH REPORT (PRIMARY SOURCE OF TRUTH)                */
  /* ===================================================== */

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const data = await reportService.getProjectReport(project._id);
        if (mounted) setReport(data);
      } catch {
        // fallback silently
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (project?._id) load();

    return () => (mounted = false);
  }, [project?._id]);

  /* ===================================================== */
  /* FALLBACK TASK STATS                                  */
  /* ===================================================== */

  const fallbackStats = useMemo(() => {
    const total = tasks.length;

    const completed = tasks.filter(
      (t) => t.status === "completed"
    ).length;

    const overdue = tasks.filter(
      (t) =>
        t.dueDate &&
        new Date(t.dueDate) < new Date() &&
        t.status !== "completed"
    ).length;

    const pending = total - completed;

    const progress =
      total === 0 ? 0 : Math.round((completed / total) * 100);

    return { total, completed, pending, overdue, progress };
  }, [tasks]);

  /* ===================================================== */
  /* FINAL DATA SOURCE                                    */
  /* ===================================================== */

  const stats = report?.tasks
    ? {
        total: report.tasks.total,
        completed: report.tasks.completed,
        pending: report.tasks.pending,
        overdue: report.tasks.overdue,
        progress:
          report.tasks.total === 0
            ? 0
            : Math.round(
                (report.tasks.completed / report.tasks.total) * 100
              ),
      }
    : fallbackStats;

  /* ===================================================== */
  /* RENDER                                                */
  /* ===================================================== */

  return (
    <div className="space-y-6">

      {/* ================= PROJECT SUMMARY ================= */}
      <Card>
        <h2 className="text-lg font-semibold mb-2">
          Project Overview
        </h2>

        {project.description ? (
          <p className="text-sm text-gray-600">
            {project.description}
          </p>
        ) : (
          <p className="text-sm text-gray-400 italic">
            No description provided
          </p>
        )}

        <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
          {project.startDate && (
            <Meta label="Start" value={formatDate(project.startDate)} />
          )}
          {project.endDate && (
            <Meta label="End" value={formatDate(project.endDate)} />
          )}
          {project.responsibleUserId?.name && (
            <Meta label="Owner" value={project.responsibleUserId.name} />
          )}
        </div>
      </Card>

      {/* ================= PROGRESS ================= */}
      <Card>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-600">
            Overall Progress
          </span>
          <span className="text-sm font-semibold">
            {stats.progress}%
          </span>
        </div>

        <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
          <div
            className="h-full bg-black transition-all"
            style={{ width: `${stats.progress}%` }}
          />
        </div>
      </Card>

      {/* ================= KPI GRID ================= */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Total Tasks" value={stats.total} />
        <Kpi label="Completed" value={stats.completed} tone="success" />
        <Kpi label="Pending" value={stats.pending} tone="warning" />
        <Kpi label="Overdue" value={stats.overdue} tone="danger" />
      </div>

      {/* ================= PHASE BREAKDOWN ================= */}
      {report?.tasks?.byPhase?.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold mb-3">
            Phase Progress
          </h3>

          <div className="space-y-3">
            {report.tasks.byPhase.map((p) => (
              <div key={p._id}>
                <div className="flex justify-between text-sm mb-1">
                  <span>{p.name}</span>
                  <span>{p.completedTasks}/{p.totalTasks}</span>
                </div>

                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-indigo-500"
                    style={{
                      width: `${
                        p.totalTasks === 0
                          ? 0
                          : (p.completedTasks / p.totalTasks) * 100
                      }%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ================= TEAM ================= */}
      <Card>
        <h3 className="text-sm font-semibold mb-3">
          Team Members
        </h3>

        {project.teamMembers?.length ? (
          <ul className="divide-y">
            {project.teamMembers.map((m) => (
              <li
                key={m._id}
                className="py-2 flex justify-between"
              >
                <div>
                  <div className="text-sm font-medium">
                    {m.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {m.email}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-400">
            No members assigned
          </p>
        )}
      </Card>

      {/* LOADING */}
      {loading && (
        <div className="text-xs text-gray-400">
          Updating insights…
        </div>
      )}
    </div>
  );
}

/* ===================================================== */
/* COMPONENTS                                            */
/* ===================================================== */

function Card({ children }) {
  return (
    <div className="rounded-xl border bg-white p-5">
      {children}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <span className="font-medium">{label}:</span> {value}
    </div>
  );
}

function Kpi({ label, value, tone = "default" }) {
  const toneMap = {
    default: "text-gray-900",
    success: "text-green-600",
    warning: "text-amber-600",
    danger: "text-red-600",
  };

  return (
    <div className="rounded-xl border bg-white p-4">
      <div className="text-sm text-gray-500">{label}</div>
      <div className={`text-2xl font-semibold ${toneMap[tone]}`}>
        {value ?? 0}
      </div>
    </div>
  );
}

function formatDate(date) {
  return new Date(date).toLocaleDateString();
}